/* eslint-disable */
import { useEffect, useState } from "react";
import { Search, AlertCircle } from "lucide-react";
import { Button, Dialog, Avatar } from "../ui";
import { boardApi } from "./api";
import { resolveAvatarUrl } from "../utils/avatarUrl";
import "./AdminBoardForm.css";

const DEFAULT_TODAY = () => new Date().toISOString().slice(0, 10);

export default function AdminBoardForm({
  open,
  mode = "board", // 'board' | 'elder'
  existing,
  onClose,
  onSaved,
}) {
  const [roles, setRoles] = useState([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [picked, setPicked] = useState(null); // { id, name, villa_number, avatar_url }
  const [search, setSearch] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);

  const [role, setRole] = useState("DIRECTOR");
  const [startDate, setStartDate] = useState(DEFAULT_TODAY());
  const [endDate, setEndDate] = useState("");
  const [termLabel, setTermLabel] = useState("");
  const [bio, setBio] = useState("");
  const [ageConfirmed, setAgeConfirmed] = useState(false);
  const [overrideRecent, setOverrideRecent] = useState(false);

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setError("");
    if (mode === "board") {
      boardApi.listRoles().then(setRoles).catch(() => {});
    }
    if (existing) {
      setPicked({
        id: existing.user.id,
        name: existing.user.name,
        villa_number: existing.user.villa_number,
        avatar_url: existing.user.avatar_url,
      });
      setRole(existing.role || "DIRECTOR");
      setStartDate(existing.start_date || DEFAULT_TODAY());
      setEndDate(existing.end_date || "");
      setTermLabel(existing.term_label || "");
      setBio(existing.bio || "");
      setAgeConfirmed(true);
    } else {
      setPicked(null);
      setRole("DIRECTOR");
      setStartDate(DEFAULT_TODAY());
      setEndDate("");
      setTermLabel("");
      setBio("");
      setAgeConfirmed(false);
      setOverrideRecent(false);
    }
  }, [open, existing, mode]);

  // Debounced owner search (board picker uses /eligible-primary-owners;
  // for elders the same endpoint surfaces a good baseline of villa
  // residents). For v1 we use the same search for both — admin
  // discretion decides who qualifies for elder.
  useEffect(() => {
    if (!pickerOpen) return;
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const r = await boardApi.listEligibleOwners(search.trim() || undefined);
        setResults(r || []);
      } catch (_) {
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 200);
    return () => clearTimeout(t);
  }, [pickerOpen, search]);

  const onSubmit = async (e) => {
    e?.preventDefault();
    if (!picked) {
      setError("Please pick a member");
      return;
    }
    setBusy(true);
    setError("");
    try {
      if (mode === "board") {
        const body = {
          user_id: picked.id,
          role,
          start_date: startDate,
          end_date: endDate || null,
          term_label: termLabel.trim() || null,
          bio: bio.trim() || null,
        };
        if (existing) await boardApi.patchMember(existing.id, body);
        else await boardApi.createMember(body);
      } else {
        const body = {
          user_id: picked.id,
          start_date: startDate,
          end_date: endDate || null,
          term_label: termLabel.trim() || null,
          bio: bio.trim() || null,
          age_confirmed: ageConfirmed,
          override_recent_board: overrideRecent,
        };
        if (existing) await boardApi.patchElder(existing.id, body);
        else await boardApi.createElder(body);
      }
      onSaved && onSaved();
    } catch (err) {
      setError(err.message || "Could not save");
    } finally {
      setBusy(false);
    }
  };

  const title = existing
    ? `Edit ${mode === "elder" ? "Elder" : "Board Member"}`
    : mode === "elder"
    ? "Add Elder"
    : "Add Board Member";

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => !o && onClose && onClose()}
      title={title}
      className="vsbf-modal"
      footer={
        <>
          <Button
            type="button"
            variant="ghost"
            onClick={onClose}
            disabled={busy}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            form="vsbf-form"
            variant="primary"
            loading={busy}
            disabled={busy || (!existing && !picked)}
          >
            {existing ? "Save changes" : "Add"}
          </Button>
        </>
      }
    >
      <form id="vsbf-form" onSubmit={onSubmit} className="vsbf-form">
        {!existing && (
          <div className="vsbf-field">
            <label>Pick a member *</label>
            {picked ? (
              <div className="vsbf-picked">
                <div className="vsbf-picked-info">
                  <Avatar
                    name={picked.name || ""}
                    imageUrl={
                      picked.avatar_url
                        ? resolveAvatarUrl(picked.avatar_url)
                        : undefined
                    }
                    size={36}
                  />
                  <div>
                    <div className="vsbf-picked-name">{picked.name}</div>
                    {picked.villa_number && (
                      <div className="vsbf-picked-villa">
                        {picked.villa_number}
                      </div>
                    )}
                  </div>
                </div>
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={() => {
                    setPicked(null);
                    setPickerOpen(true);
                  }}
                >
                  Change
                </Button>
              </div>
            ) : (
              <div className="vsbf-picker">
                <div className="vsbf-picker-search">
                  <Search size={14} />
                  <input
                    type="search"
                    placeholder="Search primary owners by name…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    onFocus={() => setPickerOpen(true)}
                  />
                </div>
                {pickerOpen && (
                  <div className="vsbf-picker-results">
                    {searching && <div className="vsbf-picker-loading">Searching…</div>}
                    {!searching && results.length === 0 && (
                      <div className="vsbf-picker-loading">No matches</div>
                    )}
                    {results.map((u) => (
                      <button
                        type="button"
                        key={u.id}
                        className="vsbf-picker-row"
                        onClick={() => {
                          setPicked(u);
                          setPickerOpen(false);
                          setSearch("");
                        }}
                      >
                        <Avatar
                          name={u.name || ""}
                          imageUrl={
                            u.avatar_url
                              ? resolveAvatarUrl(u.avatar_url)
                              : undefined
                          }
                          size={36}
                        />
                        <div>
                          <div className="vsbf-picked-name">{u.name}</div>
                          {u.villa_number && (
                            <div className="vsbf-picked-villa">
                              {u.villa_number}
                            </div>
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {mode === "board" && (
          <div className="vsbf-field">
            <label>Role *</label>
            <select
              value={role}
              onChange={(e) => setRole(e.target.value)}
            >
              {roles.map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label}
                  {r.singleton ? " (one only)" : " (up to 3)"}
                </option>
              ))}
            </select>
            {roles.length > 0 && (
              <p className="vsbf-helper">
                {roles.find((r) => r.value === role)?.responsibility}
              </p>
            )}
          </div>
        )}

        <div className="vsbf-grid">
          <div className="vsbf-field">
            <label>Start date *</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              required
            />
          </div>
          <div className="vsbf-field">
            <label>
              End date {existing ? "" : "(optional, leave blank for active)"}
            </label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>
        </div>

        <div className="vsbf-field">
          <label>Term label</label>
          <input
            type="text"
            value={termLabel}
            onChange={(e) => setTermLabel(e.target.value)}
            placeholder="e.g. 2024-2027"
            maxLength={64}
          />
        </div>

        <div className="vsbf-field">
          <label>
            {mode === "elder"
              ? "Bio / area of expertise (optional)"
              : "Bio / personal note (overrides byelaw responsibility text)"}
          </label>
          <textarea
            rows={3}
            value={bio}
            onChange={(e) => setBio(e.target.value)}
            maxLength={1000}
          />
        </div>

        {mode === "elder" && !existing && (
          <>
            <label className="vsbf-checkbox">
              <input
                type="checkbox"
                checked={ageConfirmed}
                onChange={(e) => setAgeConfirmed(e.target.checked)}
              />
              <span>
                I confirm this member is <strong>55 or older</strong>{" "}
                (required per the bye-laws)
              </span>
            </label>
            <label className="vsbf-checkbox">
              <input
                type="checkbox"
                checked={overrideRecent}
                onChange={(e) => setOverrideRecent(e.target.checked)}
              />
              <span>
                Override the "no recent Board service" check{" "}
                <em>(use only when seeding inaugural elders)</em>
              </span>
            </label>
          </>
        )}

        {error && (
          <div className="vsbf-error">
            <AlertCircle size={14} /> {error}
          </div>
        )}
      </form>
    </Dialog>
  );
}
